// Security utilities and configurations

/**
 * Content Security Policy configuration
 */
export const CSP_CONFIG = {
  'default-src': ["'self'"],
  'script-src': ["'self'", "'unsafe-inline'", "'unsafe-eval'", "https://www.youtube.com", "https://youtube.com"], // Note: unsafe-eval needed for development
  'style-src': ["'self'", "'unsafe-inline'", "https://fonts.googleapis.com"],
  'font-src': ["'self'", "https://fonts.gstatic.com"],
  // blob: required for training images and other authenticated media shown in <img>
  'img-src': ["'self'", "data:", "blob:", "https:"],
  'connect-src': ["'self'", "http://localhost:8000", "https://constantcomply.com", "https://api.constantcomply.com", "https://api.resend.com"],
  // blob: required for in-app PDF viewer (authenticated fetch → blob URL in iframe)
  'frame-src': [
    "'self'",
    "blob:",
    "http://localhost:8000",
    "https://api.constantcomply.com",
    "https://www.youtube.com",
    "https://youtube.com",
  ],
  'object-src': ["'self'", "blob:"],
  'base-uri': ["'self'"],
  'form-action': ["'self'"],
  'frame-ancestors': ["'none'"],
  'upgrade-insecure-requests': [],
};

/**
 * Security headers configuration
 */
export const SECURITY_HEADERS = {
  'X-Content-Type-Options': 'nosniff',
  'X-Frame-Options': 'DENY',
  'X-XSS-Protection': '1; mode=block',
  'Referrer-Policy': 'strict-origin-when-cross-origin',
  'Permissions-Policy': 'camera=(), microphone=(), geolocation=()',
};

/**
 * Rate limiting configuration
 */
export const RATE_LIMIT_CONFIG = {
  // API rate limits (requests per minute)
  api: {
    login: 5, // 5 login attempts per minute
    passwordReset: 3, // 3 password reset attempts per minute
    general: 100, // 100 general API requests per minute
  },
  // UI rate limits (actions per minute)
  ui: {
    formSubmit: 10, // 10 form submissions per minute
    fileUpload: 5, // 5 file uploads per minute
    search: 30, // 30 search requests per minute
  },
};

/**
 * Input validation patterns
 */
export const VALIDATION_PATTERNS = {
  email: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
  phone: /^[+]?[1-9][\d]{0,15}$/,
  employeeId: /^[A-Za-z0-9\-_]+$/,
  username: /^[a-zA-Z0-9._-]+$/,
  password: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d@$!%*?&]{8,}$/,
  url: /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/,
  filename: /^[^<>:"/\\|?*]+$/,
  html: /<[^>]*>/g,
  script: /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
  iframe: /<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi,
  javascript: /javascript:/gi,
  dataUrl: /data:/gi,
};

/**
 * File upload security configuration
 */
export const FILE_UPLOAD_CONFIG = {
  // Allowed file types
  allowedTypes: {
    images: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
    documents: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
    spreadsheets: ['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
    presentations: ['application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation'],
  },
  // Maximum file sizes (in bytes)
  maxSizes: {
    image: 5 * 1024 * 1024, // 5MB
    document: 10 * 1024 * 1024, // 10MB
    spreadsheet: 10 * 1024 * 1024, // 10MB
    presentation: 10 * 1024 * 1024, // 10MB
    general: 5 * 1024 * 1024, // 5MB
  },
  // Dangerous file extensions to block
  blockedExtensions: ['.exe', '.bat', '.cmd', '.com', '.pif', '.scr', '.vbs', '.js', '.jar', '.sh'],
};

/**
 * Password security requirements
 */
export const PASSWORD_REQUIREMENTS = {
  minLength: 8,
  maxLength: 128,
  requireUppercase: true,
  requireLowercase: true,
  requireNumbers: true,
  requireSpecialChars: false,
  maxConsecutiveChars: 3,
  commonPasswords: [
    'password', '123456', '123456789', 'qwerty', 'abc123', 'password123',
    'admin', 'letmein', 'welcome', 'monkey', '1234567890', 'dragon',
  ],
};

/**
 * Session security configuration
 */
export const SESSION_CONFIG = {
  maxAge: 24 * 60 * 60 * 1000, // 24 hours in milliseconds
  refreshThreshold: 60 * 60 * 1000, // 1 hour in milliseconds
  maxConcurrentSessions: 3,
  idleTimeout: 30 * 60 * 1000, // 30 minutes in milliseconds
};

/**
 * API security configuration
 */
export const API_SECURITY_CONFIG = {
  timeout: 10000, // 10 seconds
  retryAttempts: 3,
  retryDelay: 1000, // 1 second
  maxRedirects: 5,
  validateStatus: (status: number) => status >= 200 && status < 300,
};

/**
 * Utility function to check if a string contains potentially dangerous content
 */
export function containsDangerousContent(input: string): boolean {
  if (!input || typeof input !== 'string') {
    return false;
  }

  const dangerousPatterns = [
    VALIDATION_PATTERNS.script,
    VALIDATION_PATTERNS.iframe,
    VALIDATION_PATTERNS.javascript,
    VALIDATION_PATTERNS.dataUrl,
    /<object\b[^<]*(?:(?!<\/object>)<[^<]*)*<\/object>/gi,
    /<embed\b[^<]*(?:(?!<\/embed>)<[^<]*)*<\/embed>/gi,
    /on\w+\s*=/gi,
    /eval\s*\(/gi,
    /expression\s*\(/gi,
  ];

  return dangerousPatterns.some(pattern => pattern.test(input));
}

/**
 * Utility function to validate password strength
 */
export function validatePasswordStrength(password: string): {
  isValid: boolean;
  score: number;
  errors: string[];
  suggestions: string[];
} {
  const errors: string[] = [];
  const suggestions: string[] = [];
  let score = 0;

  if (!password || typeof password !== 'string') {
    return { isValid: false, score: 0, errors: ['Password is required'], suggestions: [] };
  }

  // Length check
  if (password.length < PASSWORD_REQUIREMENTS.minLength) {
    errors.push(`Password must be at least ${PASSWORD_REQUIREMENTS.minLength} characters long`);
  } else {
    score += 1;
  }

  if (password.length > PASSWORD_REQUIREMENTS.maxLength) {
    errors.push(`Password must be no more than ${PASSWORD_REQUIREMENTS.maxLength} characters long`);
  }

  // Character type checks
  if (PASSWORD_REQUIREMENTS.requireUppercase && !/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  } else if (/[A-Z]/.test(password)) {
    score += 1;
  }

  if (PASSWORD_REQUIREMENTS.requireLowercase && !/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
  } else if (/[a-z]/.test(password)) {
    score += 1;
  }

  if (PASSWORD_REQUIREMENTS.requireNumbers && !/\d/.test(password)) {
    errors.push('Password must contain at least one number');
  } else if (/\d/.test(password)) {
    score += 1;
  }

  if (PASSWORD_REQUIREMENTS.requireSpecialChars && !/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    errors.push('Password must contain at least one special character');
  } else if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    score += 1;
  }

  // Common password check
  if (PASSWORD_REQUIREMENTS.commonPasswords.includes(password.toLowerCase())) {
    errors.push('Password is too common. Please choose a more unique password');
    suggestions.push('Try using a combination of words, numbers, and symbols');
  }

  // Consecutive character check
  if (PASSWORD_REQUIREMENTS.maxConsecutiveChars > 0) {
    const consecutiveRegex = new RegExp(`(.)\\1{${PASSWORD_REQUIREMENTS.maxConsecutiveChars},}`, 'g');
    if (consecutiveRegex.test(password)) {
      errors.push(`Password cannot contain more than ${PASSWORD_REQUIREMENTS.maxConsecutiveChars} consecutive identical characters`);
    }
  }

  // Additional suggestions based on score
  if (score < 3) {
    suggestions.push('Consider using a mix of letters, numbers, and symbols');
  }
  if (password.length < 12) {
    suggestions.push('Longer passwords are more secure');
  }

  return {
    isValid: errors.length === 0,
    score,
    errors,
    suggestions,
  };
}

/**
 * Utility function to generate secure random strings
 */
export function generateSecureRandom(length: number = 32): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  
  for (let i = 0; i < length; i++) {
    result += chars[array[i] % chars.length];
  }
  
  return result;
}

/**
 * Utility function to check if a URL is safe
 */
export function isSafeUrl(url: string): boolean {
  try {
    const parsedUrl = new URL(url);
    
    // Only allow http and https protocols
    if (!['http:', 'https:'].includes(parsedUrl.protocol)) {
      return false;
    }
    
    // Check for suspicious patterns
    const suspiciousPatterns = [
      /javascript:/gi,
      /vbscript:/gi,
      /data:/gi,
      /file:/gi,
      /ftp:/gi,
    ];
    
    return !suspiciousPatterns.some(pattern => pattern.test(url));
  } catch {
    return false;
  }
}


